// Route-level analytics tracker. Mount once inside <BrowserRouter> and
// <AuthProvider> so it has access to both useLocation and useAuth.
//
// Responsibilities:
//   1. Initialise the session log on first tab open.
//   2. Identify the logged-in user in PostHog (uses Firebase UID as distinct_id).
//   3. Fire a pageview_extended event on every route change, enriched with:
//      - previous_page      → where the user was before this navigation (exit-intent signal)
//      - most_visited_page_ever → their "hub" page (interest signal)
//      - session_frequency  → daily return count (high-intent window-shopper signal)
//   4. Register super properties so every downstream event carries these signals.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import * as posthog from "./safePosthog";
import { useAuth } from "../contexts/AuthProvider";
import {
  initSession,
  recordPageVisit,
  getLastVisited,
  getMostVisited,
  getDailyFrequency,
} from "./session";
import { AnalyticsEvents } from "./events";
import { toPersonPhone } from "./personPhone";
import { saveUtmAttribution, getUtmAttribution } from "../utils/utmAttribution";
import { saveEmailAttribution, getEmailAttribution } from "../utils/emailAttribution";

export function RouteAnalyticsTracker() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const userFirebaseId = user?.firebase_id;
  const userPhone = user?.phone;
  const userEmail = user?.email;

  // One-time session initialisation (idempotent — safe on re-renders)
  useEffect(() => {
    initSession();
  }, []);

  // Persist UTM params to localStorage on first UTM-bearing page load.
  // Also registers them as PostHog super properties so every subsequent event
  // (even in future sessions) carries the original campaign context.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    saveUtmAttribution(searchParams);

    const attribution = getUtmAttribution();
    if (attribution?.utm_source) {
      posthog.register({
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_content: attribution.utm_content,
        utm_promo_code: attribution.promo_code,
      });
      posthog.setPersonProperties({
        last_utm_source: attribution.utm_source,
        last_utm_medium: attribution.utm_medium,
        last_utm_campaign: attribution.utm_campaign,
        last_utm_promo_code: attribution.promo_code,
      });
    }
  }, []);

  // Persist the `emailaddress` param (used by cold-outreach links) to localStorage
  // and register it as a PostHog super/person property so every event fired during
  // this and future sessions carries it — lets engagement be matched to a lead's
  // email even without a form submission. Deliberately NOT passed to identify():
  // like firebase_id vs. email above, we don't want PII as PostHog's primary key.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    saveEmailAttribution(searchParams);

    const emailAttribution = getEmailAttribution();
    if (emailAttribution?.emailaddress) {
      posthog.register({ emailaddress: emailAttribution.emailaddress });
      posthog.setPersonProperties({
        last_known_emailaddress: emailAttribution.emailaddress,
      });
    } else {
      // No valid attribution — either never captured, or the 30-day TTL
      // lapsed. PostHog's own super-property persistence has no expiry of
      // its own, so without this the browser would keep tagging every event
      // with a stale email forever instead of actually expiring after 30 days.
      posthog.unregister("emailaddress");
    }
  }, []);

  // Identify user as soon as the auth context resolves.
  // Using firebase_id (not email) as distinct_id keeps the identifier stable
  // across email changes and avoids PII in PostHog's primary key. Email is
  // still passed as a person property (not the distinct_id) so PostHog's
  // person profile and session replay list show a readable name instead of
  // the raw Firebase UID. Phone is set for the same reason: a phone-first
  // customer has no email, so it is the only readable label they have.
  useEffect(() => {
    if (!userFirebaseId) return;
    posthog.identify(userFirebaseId, {
      email: userEmail,
      phone_verified: !!userPhone,
      phone: toPersonPhone(userPhone),
    });
  }, [userFirebaseId, userEmail, userPhone]);

  // Fire pageview_extended on every route change.
  // Capture BEFORE recordPageVisit so previous_page reflects the page the
  // user navigated FROM, not the one they just arrived on.
  useEffect(() => {
    const previousPage = getLastVisited();
    const mostVisited = getMostVisited();
    const sessionFreq = getDailyFrequency();

    posthog.capture(AnalyticsEvents.PAGEVIEW_EXTENDED, {
      page_path: pathname,             // page the user is NOW on
      previous_page: previousPage,     // page they navigated FROM
      most_visited_page_ever: mostVisited,
      session_frequency: sessionFreq,
    });

    // Stamp super properties so every event fired on this page is enriched
    posthog.register({
      current_page: pathname,
      session_frequency: sessionFreq,
      most_visited_page_ever: mostVisited,
    });

    // Record AFTER capture so the next pageview reads this page as previous_page
    recordPageVisit(pathname);
  }, [pathname]);

  return null;
}
